import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, X, User, Users, Trophy, FileText, DollarSign, 
  Building, Home, Receipt, MapPin, Loader2 
} from "lucide-react";
import { useAuth, API } from "../App";
import axios from "axios";
import debounce from "lodash/debounce";

const typeIcons = {
  player: <User className="w-4 h-4" />,
  parent: <Users className="w-4 h-4" />,
  user: <User className="w-4 h-4" />,
  program: <Trophy className="w-4 h-4" />,
  registration: <FileText className="w-4 h-4" />,
  transaction: <DollarSign className="w-4 h-4" />,
  coach: <Users className="w-4 h-4" />,
  household: <Home className="w-4 h-4" />,
  invoice: <Receipt className="w-4 h-4" />,
  facility: <MapPin className="w-4 h-4" />,
  organization: <Building className="w-4 h-4" />,
};

const typeColors = {
  player: "bg-blue-100 text-blue-700",
  parent: "bg-purple-100 text-purple-700",
  user: "bg-slate-100 text-slate-700",
  program: "bg-emerald-100 text-emerald-700",
  registration: "bg-amber-100 text-amber-700",
  transaction: "bg-green-100 text-green-700",
  coach: "bg-indigo-100 text-indigo-700",
  household: "bg-pink-100 text-pink-700",
  invoice: "bg-orange-100 text-orange-700",
  facility: "bg-cyan-100 text-cyan-700",
  organization: "bg-slate-100 text-slate-700",
};

const AdminSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { token } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search function
  const searchApi = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/search`, {
          params: { q: searchQuery, limit: 15 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setResults(response.data.results || []);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [token]
  );

  useEffect(() => {
    if (query.length >= 2) {
      setLoading(true);
      searchApi(query);
    } else {
      setResults([]);
    }
  }, [query, searchApi]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) {
      if (e.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleResultClick = (result) => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    navigate(result.url);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  const typeLabels = {
    player: "Players",
    parent: "Parents",
    user: "Users",
    program: "Programs",
    registration: "Registrations",
    transaction: "Transactions",
    coach: "Coaches & Staff",
    household: "Households",
    invoice: "Invoices",
    facility: "Facilities",
    organization: "Organizations",
  };

  return (
    <div className="relative flex-1 max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search players, programs, registrations..."
          className="w-full pl-10 pr-10 py-2 bg-slate-100 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all placeholder:text-slate-400"
          data-testid="admin-global-search"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-200 transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-100 max-h-[70vh] overflow-y-auto z-50"
          data-testid="search-results-dropdown"
        >
          {loading && results.length === 0 ? (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : results.length === 0 && query.length >= 2 ? (
            <div className="p-4 text-center text-slate-500">
              <Search className="w-5 h-5 mx-auto mb-2 opacity-50" />
              <span className="text-sm">No results found for "{query}"</span>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(groupedResults).map(([type, items]) => (
                <div key={type}>
                  <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">
                    {typeLabels[type] || type}
                  </div>
                  {items.map((result, idx) => {
                    const globalIndex = results.indexOf(result);
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className={`w-full px-3 py-2.5 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors ${
                          selectedIndex === globalIndex ? "bg-slate-100" : ""
                        }`}
                        data-testid={`search-result-${result.type}-${result.id}`}
                      >
                        <div className={`p-2 rounded-lg ${typeColors[result.type] || "bg-slate-100 text-slate-600"}`}>
                          {typeIcons[result.type] || <FileText className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {result.title}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {result.subtitle}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {results.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-400 flex items-center justify-between">
              <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
              <span className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">↑↓</kbd>
                <span>navigate</span>
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">↵</kbd>
                <span>select</span>
                <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200 text-[10px]">esc</kbd>
                <span>close</span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSearch;
